import api from '../api';
import { ResultadoFinal } from '../model/banca/votacao/Votacao';
import { VotoCandidato } from '../model/banca/votacao/Voto';
import { Candidato, ResultadoCandidato, StatusCandidato } from '../model/Candidato/Candidato';
import { CandidatoResponsavel } from '../model/Candidato/CandidatoResponsavel';

export interface CandidatoOnlyResponse {
  id: string;
  candidatoEditais: CandidatoEditalResponse[];
  nome: string;
  nomeSocial: string | null;
  cpf: string;
  passaporte: string | null;
  email: string;
  status: StatusCandidato;
  autodeclaracaoRacial?: string | null;
  candidatoResponsavel: CandidatoResponsavelResponse | null;
  duplicidade: boolean;
  temFoto: boolean;
}

export interface CandidatoEditalResponse {
  id: string;
  inscricao: string;
  candidatoId: string;
  editalId: string;
  nomeEdital: string;
}

export interface CandidatoResponsavelResponse {
  id: string;
  nome: string;
  nomeSocial: string | null;
  cpf: string;
  passaporte: string | null;
  email: string;
}


export interface PaginatedResponse<T> {
    content: T[];              // Array dos itens da página atual
    totalElements: number;     // Total de itens em todas as páginas
    totalPages: number;        // Total de páginas disponíveis
    size: number;              // Número de itens por página
    number: number;            // Número da página atual (começa em 0)
    first: boolean;            // Indica se é a primeira página
    last: boolean;             // Indica se é a última página
    empty?: boolean;           // Opcional: indica se a página está vazia
    numberOfElements?: number;  // Opcional: número de elementos na página atual
    pageable?: {               // Opcional: informações sobre a paginação
        sort: {
            sorted: boolean;
            unsorted: boolean;
            empty: boolean;
        };
        offset: number;
        pageNumber: number;
        pageSize: number;
        paged: boolean;
        unpaged: boolean;
    };
    sort?: {                  // Opcional: informações sobre a ordenação
        sorted: boolean;
        unsorted: boolean;
        empty: boolean;
    };
}

export default class CandidatoViewModel {
    static async obterVideoCandidato(bancaId: string, candidatoId: string): Promise<Blob> {
        try {
            const response = await api.get(`/bancas/${bancaId}/candidatos/${candidatoId}/video`, {
                responseType: 'blob',
                validateStatus: function (status) {
                    // Aceita apenas códigos de status 2xx
                    return status >= 200 && status < 300;
                },
            });

            // Retorna o blob se a resposta foi bem-sucedida
            return response.data;
        } catch (error: any) {
            // Checa se o erro é relacionado a status 4xx ou 5xx
            if (error.response) {
                console.error(`Erro ${error.response.status}: ${error.response.statusText}`);
            } else {
                console.error('Erro ao obter o vídeo do candidato:', error);
            }
            throw error;
        }
    }

    static async marcarCandidatoComoAusente(bancaId: string, candidatoId: string): Promise<void> {
        try {
            await api.patch(`/bancas/${bancaId}/candidatos/${candidatoId}/ausencia`);
        } catch (error) {
            console.error('Erro ao marcar candidato como ausente:', error);
            throw error;
        }
    }

    // Função auxiliar para mapear o voto string para enum VotoCandidato
    private static mapVotoToEnum(voto: string): VotoCandidato {
        switch (voto) {
            case 'VALIDADO':
                return VotoCandidato.VALIDADO;
            case 'NAO_VALIDADO':
                return VotoCandidato.NAO_VALIDADO;
            default:
                return VotoCandidato.NAO_VALIDADO;
        }
    }

    static async desvincularCandidato(processoId: string, bancaId: string, candidatoId: string) {
        try {
            await api.delete(
                `/processos/${processoId}/bancas/${bancaId}/candidatos/${candidatoId}/desvinculo`
            );
        } catch (error) {
            throw error;
        }
    }

    static async vincularCandidato(processoId: string, bancaId: string, candidatoId: string): Promise<boolean> {
        try {
            const response = await api.post(
                `/processos/${processoId}/bancas/${bancaId}/candidatos/${candidatoId}/vinculo`
            );
            return response.status >= 200 && response.status < 300;
        } catch (error) {
            throw error;
        }
    }

    // Função auxiliar para mapear status string para enum StatusCandidato
    private static mapStatusToEnum(status: string): StatusCandidato {
        switch (status) {
            case 'NAO_AVALIADO':
                return StatusCandidato.NAO_AVALIADO;
            case 'INCONCLUSIVO':
                return StatusCandidato.INCONCLUSIVO;
            case 'VALIDADO':
                return StatusCandidato.VALIDADO;
            case 'VALIDADO_ANTERIORMENTE':
                return StatusCandidato.VALIDADO_ANTERIORMENTE;
            case 'NAO_VALIDADO':
                return StatusCandidato.NAO_VALIDADO;
            case 'DELETADO':
                return StatusCandidato.DELETADO;
            case 'AUSENTE':
                return StatusCandidato.AUSENTE;
            default:
                return StatusCandidato.NAO_AVALIADO;
        }
    }

    // Função para mapear candidatoResponsavel, caso ele exista
    private static mapCandidatoResponsavel(candidatoResponsavel: any): CandidatoResponsavel | null {
        if (candidatoResponsavel) {
            return {
                ...candidatoResponsavel,
            };
        }
        return null; // Retorna null se não houver um candidatoResponsavel
    }

    // Mapear o array de candidatos para garantir que o status e candidatoResponsavel estejam no formato correto
    private static mapCandidatos(candidatos: any[]): Candidato[] {
        return candidatos.map((c) => ({
            ...c,
            status: this.mapStatusToEnum(c.status), // Mapeia o status para o enum
            candidatoResponsavel: this.mapCandidatoResponsavel(c.candidatoResponsavel), // Mapeia o candidatoResponsavel
        }));
    }

    static async getCandidatosByProcesso(processoId: string): Promise<Candidato[]> {
        try {
            const response = await api.get(`/processos/${processoId}/candidatos`);
            const candidatos = response.data.processo.candidatos;
            return this.mapCandidatos(candidatos); // Chama a função de mapeamento
        } catch (error) {
            console.error('Error fetching candidatos:', error);
            throw error;
        }
    }

    static async getCandidatosByProcessoPaginated(
        processoId: string,
        page: number = 1,
        limit: number = 200,
        searchTerm: string = '',
        bancaId: string = '',
        recurso?: boolean
    ): Promise<Candidato[]> {
        try {
            if (recurso === undefined) {
                page = page - 1;
                const params = {
                    page,
                    limit,
                    searchTerm,
                    bancaId
                };

                const response = await api.get(`/processos/${processoId}/candidatos`, { params });

                const candidatos = response.data.content || [];
                return this.mapCandidatos(candidatos);
            }
            else {
                page = page - 1;
                const params = {
                    page,
                    limit,
                    searchTerm,
                    bancaId,
                    recurso
                };

                const response = await api.get(`/processos/${processoId}/candidatos`, { params });

                const candidatos = response.data.content || [];
                return this.mapCandidatos(candidatos);
            }

        } catch (error) {
            console.error('Error fetching paginated candidatos:', error);
            throw error;
        }
    }

    static async getCandidatosByProcessoPaginatedComTotal(
        processoId: string,
        page: number = 1,
        limit: number = 200,
        searchTerm: string = '',
        bancaId: string = '',
        recurso?: boolean,
        exibirDeletados?: boolean
    ): Promise<{ candidatos: Candidato[]; total: number }> {
        try {
            const adjustedPage = page - 1; // página começa em 1 no frontend, backend usa 0

            const params: any = {
                page: adjustedPage,
                limit,
                searchTerm,
                bancaId,
                exibirDeletados
            };

            if (recurso !== undefined) {
                params.recurso = recurso;
            }

            const response = await api.get(`/processos/${processoId}/candidatos`, { params });

            const candidatos = this.mapCandidatos(response.data.content || []);
            const total = response.data.totalElements || 0;

            return { candidatos, total };
        } catch (error) {
            console.error('Error fetching paginated candidatos:', error);
            throw error;
        }
    }


    static async getCandidatosByProcessoPaginatedVinculacao(
        processoId: string,
        page: number = 1,
        limit: number = 920,
        searchTerm: string = '',
        bancaId: string = '',
    ): Promise<Candidato[]> {
        try {
            page = page - 1;
            const params = {
                page,
                limit,
                searchTerm,
                bancaId
            };

            const response = await api.get(`/processos/${processoId}/candidatos/vinculacao`, { params });

            const candidatos = response.data.content || [];
            return this.mapCandidatos(candidatos);
        } catch (error) {
            console.error('Error fetching paginated candidatos:', error);
            throw error;
        }
    }

    // Função para mapear o resultado final
    private static mapResultadoFinalToEnum(result: string): ResultadoFinal | null {
        switch (result) {
            case 'VALIDADO':
                return ResultadoFinal.VALIDADO;
            case 'NAO_VALIDADO':
                return ResultadoFinal.NAO_VALIDADO;
            case 'AUSENTE':
                return ResultadoFinal.AUSENTE;
            default:
                return ResultadoFinal.NAO_DEFINIDO; // Valor padrão caso resultadoFinal seja null ou desconhecido
        }
    }

    // Função para mapear o resultado do candidato
    private static mapResultadoToEnum(result: string): ResultadoCandidato {
        switch (result) {
            case 'VALIDADO':
                return ResultadoCandidato.VALIDADO;
            case 'NAO_VALIDADO':
                return ResultadoCandidato.NAO_VALIDADO;
            case 'NULO':
                return ResultadoCandidato.NULO;
            default:
                return ResultadoCandidato.NULO; // Valor padrão caso resultado seja null ou desconhecido
        }
    }

    static async getCandidatoById(candidatoId: string): Promise<Candidato> {
        try {
            const response = await api.get(`/candidatos/${candidatoId}`);
            const candidato = response.data.processo.candidatos.find((c: Candidato) => c.id === candidatoId);
            if (!candidato) throw new Error('Candidato não encontrado');
            return {
                ...candidato,
                status: this.mapStatusToEnum(candidato.status), // Mapeia o status para o enum
                candidatoResponsavel: this.mapCandidatoResponsavel(candidato.candidatoResponsavel), // Mapeia o candidatoResponsavel
            };
        } catch (error) {
            console.error('Error fetching candidato by ID:', error);
            throw error;
        }
    }

    static async postCandidatosCSVJSON(processoId: string, base64String: string): Promise<any> {
        try {
            const response = await api.post(
                `/processos/${processoId}/candidatos/arquivo`,
                {
                    base64: base64String,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response;
        } catch (error) {
            console.error('Error uploading candidatos CSV:', error);
            throw error;
        }
    }

    static async patchCandidatos(processoId: string, candidatosAtualizados: any): Promise<any> {
        try {
            const response = await api.patch(
                '/candidatos/dadoscandidatos',
                {
                    processo: {
                        identificador: processoId,
                        ...candidatosAtualizados,
                    },
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error patching candidatos:', error);
            throw error;
        }
    }

    static async obterVideoCandidatoUrl(bancaId: string, candidatoId: string): Promise<{ url: string }> {
        try {
            const response = await api.get(`/bancas/${bancaId}/candidatos/${candidatoId}/videoUrl`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            // Retorna o blob se a resposta foi bem-sucedida
            return response.data;
        } catch (error: any) {
            // Checa se o erro é relacionado a status 4xx ou 5xx
            if (error.response) {
                console.error(`Erro ${error.response.status}: ${error.response.statusText}`);
            } else {
                console.error('Erro ao obter o vídeo do candidato:', error);
            }
            throw error;
        }
    }

    /**
     * Obtém candidatos de uma banca com paginação, seguindo a estrutura do backend
     * @param bancaId ID da banca
     * @param page Número da página (começa em 0)
     * @param size Tamanho da página (padrão 20)
     * @param sort Campo para ordenação (padrão 'nome')
     */
    static async getCandidatosByBancaNovo(
        bancaId: string,
        page: number = 0,
        size: number = 20,
        sort: string = 'nome'
    ): Promise<PaginatedResponse<Candidato>> {
        try {
            const params = {
                page,
                size,
                sort
            };

            const response = await api.get(`/bancas/${bancaId}/candidatos`, { params });

            const content = (response.data.content || []).map((c: any) => ({
                id: c.candidatoId,
                inscricao: c.inscricao,
                nome: c.nome,
                nomeSocial: c.nomeSocial,
                cpf: '',
                passaporte: '',
                email: '',
                status: this.mapStatusToEnum(c.statusCandidato),
                resultado: this.mapResultadoFinalToEnum(c.resultado),
                candidatoResponsavel: null,
                file_name: c.file_name,
                duplicidade: false
            }));

            return {
                content,
                totalElements: response.data.totalElements,
                totalPages: response.data.totalPages,
                size: response.data.size,
                number: response.data.number,
                first: response.data.first,
                last: response.data.last
            };
        } catch (error) {
            console.error('Error fetching candidatos by banca:', error);
            throw error;
        }
    }

    /**
     * Obtém todos os candidatos de um processo (sem paginação)
     * @param processoId ID do processo
     * @param exibirDeletados Se deve incluir candidatos deletados
     */
    static async getAllCandidatosByProcesso(
        processoId: string,
        exibirDeletados: boolean = false
    ): Promise<Candidato[]> {
        try {
            const params = {
                exibirDeletados
            };

            const response = await api.get(`/processos/${processoId}/candidatos/all`, { params });

            return (response.data || []).map((c: CandidatoOnlyResponse) => ({
                id: c.id,
                nome: c.nome,
                nomeSocial: c.nomeSocial,
                cpf: c.cpf,
                passaporte: c.passaporte,
                email: c.email,
                status: this.mapStatusToEnum(c.status),
                autodeclaracaoRacial: c.autodeclaracaoRacial || null,
                candidatoResponsavel: this.mapCandidatoResponsavel(c.candidatoResponsavel),
                duplicidade: c.duplicidade,
                resultado: undefined,
                file_name: undefined
            }));
        } catch (error) {
            console.error('Error fetching all candidatos by processo:', error);
            throw error;
        }
    }

    /**
     * Obtém candidatos para vinculação técnico com filtros e paginação independentes
     */
    static async getCandidatosVinculacaoTecnico(
        processoId: string,
        bancaId: string,
        params: {
            // Filtros candidatos não vinculados
            nomeNaoVinculado?: string;
            nomeSocialNaoVinculado?: string;
            inscricaoNaoVinculado?: string;
            cpfNaoVinculado?: string;
            passaporteNaoVinculado?: string;
            emailNaoVinculado?: string;
            statusNaoVinculado?: string;
            hasRecursoNaoVinculado?: boolean;
            pageNaoVinculado?: number;
            sizeNaoVinculado?: number;
            sortNaoVinculado?: string;
            
            // Filtros candidatos vinculados
            nomeVinculado?: string;
            nomeSocialVinculado?: string;
            inscricaoVinculado?: string;
            cpfVinculado?: string;
            passaporteVinculado?: string;
            emailVinculado?: string;
            statusVinculado?: string;
            pageVinculado?: number;
            sizeVinculado?: number;
            sortVinculado?: string;
        } = {}
    ) {
        try {
            const response = await api.get(
                `/processos/${processoId}/bancas/${bancaId}/candidatos/vinculacao-tecnico`,
                { params }
            );

            // Mapear candidatos não vinculados
            const candidatosNaoVinculados = {
                ...response.data.candidatosNaoVinculados.candidatos,
                content: response.data.candidatosNaoVinculados.candidatos.content.map((item: any) => ({
                    candidato: {
                        id: item.id,
                        inscricao: item.inscricao,
                        nome: item.nome,
                        nomeSocial: item.nomeSocial,
                        cpf: item.cpf,
                        passaporte: item.passaporte,
                        email: item.email,
                        status: this.mapStatusToEnum(item.status),
                        candidatoResponsavel: null,
                        duplicidade: false,
                        resultado: undefined,
                        file_name: undefined,
                        candidatoEditais: item.candidatoEditais,
                        responsavel: item.responsavel
                    },
                    hasRecurso: item.hasRecurso,
                    hasEditalEmComum: item.hasEditalEmComum,
                    hasResponsavel: item.hasResponsavel
                }))
            };

            // Mapear candidatos vinculados
            const candidatosVinculados = {
                ...response.data.candidatosVinculados.candidatosBanca,
                content: response.data.candidatosVinculados.candidatosBanca.content.map((item: any) => ({
                    id: item.id,
                    candidato: {
                        id: item.candidato.id,
                        inscricao: item.candidato.inscricao,
                        nome: item.candidato.nome,
                        nomeSocial: item.candidato.nomeSocial,
                        cpf: item.candidato.cpf,
                        passaporte: item.candidato.passaporte,
                        email: item.candidato.email,
                        status: this.mapStatusToEnum(item.candidato.status),
                        candidatoResponsavel: null,
                        duplicidade: false,
                        resultado: undefined,
                        file_name: undefined,
                        responsavel: item.candidato.responsavel
                    },
                    resultado: item.resultado,
                    dataInicio: item.dataInicio,
                    dataFim: item.dataFim,
                    statusVotacao: item.statusVotacao,
                    hasRecurso: item.candidato.hasRecurso,
                    hasResponsavel: item.candidato.hasResponsavel
                }))
            };

            return {
                candidatosNaoVinculados,
                candidatosVinculados,
                estatisticas: response.data.estatisticas,
                bancaTemEditais: response.data.bancaTemEditais
            };
        } catch (error) {
            console.error('Error fetching candidatos vinculacao tecnico:', error);
            throw error;
        }
    }

    static async updateCandidato(
        processoId: string,
        candidatoId: string,
        data: any
    ): Promise<any> {
        try {
            const response = await api.put(
                `/processos/${processoId}/candidatos/${candidatoId}`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating candidato:', error);
            throw error;
        }
    }

    static async getCandidatosResumo(processoId: string): Promise<any> {
        try {
            const response = await api.get(`/processos/${processoId}/candidatos/resumo`);
            return response.data;
        } catch (error) {
            console.error('Error fetching candidatos resumo:', error);
            throw error;
        }
    }
}
